import {observable, action} from "mobx";
import clientErrorLogger from "util/clientErrorLogger";
import moment from "moment";
import isNil from "lodash/isNil";
import split from "lodash/split";
import forEach from "lodash/forEach";
import {throttle} from "lodash";

class SessionModel {
    @observable inactivityTimeRemaining;
    @observable serverTimeRemaining;
    @observable user;
    @observable isLoggedIn;
    @observable currentUtc;
    @observable flashMessage;
    @observable isImpersonating;
    @observable userAccounts;
    @observable modifiedBy;
    @observable features;
    @observable unreadNotificationCount;
    @observable notificationCount;
    @observable siteFooterItems;
    @observable featureToggles;
    @observable requestInfoUrl;
    @observable isExpiringModalOpen = false;

    activityHandler = null;
    visibilityHandler = null;
    broadcastChannel = null;
    channelName = 'session-inactivity';
    documentHidden = false;

    constructor(props, dispatcher, apiGetter) {
        this.dispatcher = dispatcher;
        this.apiGetter = apiGetter;

        if (!props) {
            // not having session props is okay because we can load them async
            return;
        }

        this.update(props);
        this.inactivityTimeRemaining = this.maxInactivitySeconds;
    }

    @action
    initBroadcastChannel() {
        if (typeof BroadcastChannel === 'undefined') {
            return;
        }

        this.broadcastChannel = new BroadcastChannel(this.channelName);
        this.broadcastChannel.onmessage = (event) => {
            switch (event.data.type) {
                case 'RESET_INACTIVITY':
                    this.resetInactivityTimer(true);
                    break;
                case 'SESSION_EXPIRED':
                    this.dispatcher.emit("SESSION_EXPIRED");
                    break;
                case 'LOGOUT':
                    this.dispatcher.emit("LOGOUT");
                    break;
                default:
                    break;
            }
        };

        this.broadcastReset();
    }

    broadcastReset() {
        if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({type: 'RESET_INACTIVITY'});
        }
    }

    broadcastSessionExpired() {
        if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({type: 'SESSION_EXPIRED'});
        }
    }

    broadcastLogout() {
        if (this.broadcastChannel) {
            this.broadcastChannel.postMessage({type: 'LOGOUT'});
        }
    }

    @action
    tick() {
        if (this.documentHidden) {
            return;
        }

        this.inactivityTimeRemaining -= 1;
        this.serverTimeRemaining -= 1;
        this.checkServerExpiration();
        this.checkInactivity();
    }

    @action
    restart() {
        if (this.isExpiringModalOpen) {
            return;
        }

        this.resetInactivityTimer(false);
    }

    @action
    resetInactivityTimer(fromBroadcast = false) {
        const shouldRefresh = this.serverTimeRemaining < this.warningThreshold || this.inactivityTimeRemaining < this.warningThreshold;

        this.inactivityTimeRemaining = this.maxInactivitySeconds;

        if (shouldRefresh) {
            this.dispatcher.emit("LOAD_SESSION_DATA", {forceRefresh: true});
        }

        if (!fromBroadcast) {
            this.broadcastReset();
        }
    }

    @action
    extendSession() {
        this.isExpiringModalOpen = false;
        this.restart();
        this.dispatcher.emit("HIDE_SESSION_EXPIRING_MODAL");
    }

    @action
    update(props) {
        this.user = props.user;
        this.loginPath = props.loginPath;
        this.isLoggedIn = props.isLoggedIn;
        this.serverTimeRemaining = props.serverTimeRemaining;
        this.maxInactivitySeconds = !isNil(props.inactivityThreshold) ? props.inactivityThreshold : 600;
        this.warningThreshold = !isNil(props.expirationWarningSeconds) ? props.expirationWarningSeconds : 60;
        this.environmentName = props.environmentName;
        this.currentUtc = props.currentUtc;
        this.timeWarp = moment.duration(props.timeWarp);
        this.flashMessage = props.flashMessage;
        this.isImpersonating = props.isImpersonating;
        this.userAccounts = props.userAccounts;
        this.modifiedBy = props.modifiedBy;
        this.features = props.features;
        this.unreadNotificationCount = props.unreadNotificationCount;
        this.notificationCount = props.notificationCount;
        this.baseAddress = props.baseAddress;
        this.siteFooterModel = props.siteFooterModel;
        this.featureToggles = props.featureToggles;
        this.requestInfoUrl = props.requestInfoUrl;
    }

    @action
    loadFromServer(forceRefresh, retryDelay) {
        const self = this;
        if (!this.user || forceRefresh) {
            // If you make any changes to this at all, be sure to test various registration flows (slug, access page, etc)
            this.apiGetter.fetch("/api/session",
                (res) => {
                    // Don't ever delete the user from the session, since some portal-specific code assumes that it will always be there.
                    if (!res.user) {
                        res.user = self.user;
                    }

                    // update this model with new data
                    self.update(res);

                    if (res.isLoggedIn) {
                        self.start(forceRefresh);
                    }
                },
                () => {
                    // There's not really a good reason for this to fail, so maybe it's a connection error? Try to reload it a few more times.
                    retryDelay = (retryDelay || 0) + 5000;
                    if (retryDelay < 15000) { // Abandon after three attempts.
                        setTimeout(() => {
                            self.loadFromServer(forceRefresh, retryDelay);
                        }, retryDelay);
                    } else {
                        clientErrorLogger.notify("Could not load session data", "warning");
                    }
                }
            );
        } else if (this.isLoggedIn) {
            this.start(false);
        }
    }

    @action
    start(forceRefresh) {
        if (!this.serverTimeRemaining) {
            // if there is no server time defined, don't start the timer on the client
            // this is used on the intranet to prevent the timer
            return;
        }

        if (!this.started) {
            let tickInterval = 1000;
            this.activityHandler = throttle(this.restart.bind(this), 500, {leading: true, trailing: true});
            this.timer = setInterval(this.tick.bind(this), tickInterval);
            this.bindActivityEvents();

            const handleVisibilityChange = () => {
                this.documentHidden = document.hidden;
                if (!document.hidden) {
                    this.restart();
                    this.dispatcher.emit("LOAD_SESSION_DATA", {forceRefresh: true});
                }
            };
            document.addEventListener('visibilitychange', handleVisibilityChange);
            this.visibilityHandler = handleVisibilityChange;

            this.initBroadcastChannel();
            this.started = true;
        }

        this.restart();
    }

    stop() {
        this.unbindActivityEvents();

        if (this.activityHandler && this.activityHandler.cancel) {
            this.activityHandler.cancel();
        }

        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }

        if (this.broadcastChannel) {
            this.broadcastChannel.close();
            this.broadcastChannel = null;
        }

        clearInterval(this.timer);
        this.timer = undefined;
        this.started = false;
    }

    addListenerMulti(el, eventNames, fn) {
        let events = split(eventNames, " ");
        for (let i = 0, iLen = events.length; i < iLen; i++) {
            if (el.addEventListener) {
                el.addEventListener(events[i], fn, true);
            } else {
                el.attachEvent("on" + events[i], fn);
            }
        }
    }

    bindActivityEvents() {
        let bindingEl = window ? window : document;
        if (bindingEl) {
            this.addListenerMulti(bindingEl, "touchstart touchmove touchend keydown keyup click mousemove scroll wheel", this.activityHandler);
        }
    }

    unbindActivityEvents() {
        let bindingEl = window ? window : document;
        if (bindingEl) {
            this.removeListenerMulti(bindingEl, "touchstart touchmove touchend keydown keyup click mousemove scroll wheel", this.activityHandler);
        }
    }

    removeListenerMulti(el, eventNames, fn) {
        forEach(split(eventNames, " "), evt => {
            if (el.removeEventListener) {
                el.removeEventListener(evt, fn, true);
            } else {
                el.detachEvent("on" + evt, fn);
            }
        });
    }

    // checks the server-side session tick in seconds
    checkServerExpiration() {
        let secsLeft = this.serverTimeRemaining;
        if (secsLeft <= 0) {
            this.dispatcher.emit("SESSION_EXPIRED");
            this.isExpiringModalOpen = false;
            this.broadcastSessionExpired();
            this.stop();
        } else if (secsLeft < this.warningThreshold && secsLeft < this.inactivityTimeRemaining) {
            // send a request to extend server-side session
            this.dispatcher.emit("LOAD_SESSION_DATA", {forceRefresh: true});
        }
    }

    // check on the client inactivity counter
    checkInactivity() {
        if (this.inactivityTimeRemaining <= 0) {
            this.dispatcher.emit("SESSION_EXPIRED");
            this.isExpiringModalOpen = false;
            this.broadcastSessionExpired();
            this.stop();
        } else if (this.inactivityTimeRemaining <= this.warningThreshold) {
            this.isExpiringModalOpen = true;
            this.dispatcher.emit("SESSION_EXPIRING", {remainingTime: this.inactivityTimeRemaining});
        } else {
            this.dispatcher.emit("HIDE_SESSION_EXPIRING_MODAL");
            this.isExpiringModalOpen = false;
        }
    }

    getLoginPath() {
        return this.loginPath;
    }

    isStarted() {
        return this.started;
    }

    getCurrentUtcNow() {
        return moment().add(this.timeWarp);
    }
}

export default SessionModel;
